// FAKHIZ — storefront behaviour. No dependencies, no libraries.
/* fk-iife */
// Seven sections each include this file and a classic script runs once per
// tag. At top level the second copy dies on "Identifier $ has already been
// declared", so the module is scoped instead. Re-runs are harmless: every
// initialiser claims its element via claim().
(() => {

  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => [...root.querySelectorAll(sel)];

  /**
   * Several sections each include this file, and a classic script runs once per
   * tag — so boot() fires many times per page. Every initialiser claims its
   * element through this first, otherwise listeners stack up and a single click
   * fires as many times as there are script tags.
   */
  const claim = (el, key) => {
    if (!el || el.dataset[key] === 'true') return false;
    el.dataset[key] = 'true';
    return true;
  };

  /* ---------- Header ---------- */

  const initHeader = () => {
    const header = $('[data-fk-header]');
    if (!claim(header, 'fkHeader')) return;

    const onScroll = () => header.classList.toggle('is-stuck', window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });

    const search = $('[data-fk-search]', header);
    $('[data-fk-search-toggle]', header)?.addEventListener('click', () => {
      const open = search.classList.toggle('is-open');
      if (open) $('input', search)?.focus();
    });

    const drawer = $('[data-fk-drawer]');
    if (!drawer) return;

    const open = () => {
      drawer.hidden = false;
      // Forcing layout here rather than waiting on a frame: rAF never fires on a
      // backgrounded tab, which would leave the drawer invisible but the page
      // locked behind it.
      void drawer.offsetWidth;
      drawer.classList.add('is-open');
      document.body.style.overflow = 'hidden';
    };

    const close = () => {
      drawer.classList.remove('is-open');
      document.body.style.overflow = '';
      setTimeout(() => {
        drawer.hidden = true;
      }, 320);
    };

    $('[data-fk-drawer-open]')?.addEventListener('click', open);
    $$('[data-fk-drawer-close]', drawer).forEach((el) => el.addEventListener('click', close));
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && drawer.classList.contains('is-open')) close();
    });
  };

  /* ---------- Gallery ---------- */

  const initGallery = (root) => {
    const slides = $('[data-fk-slides]', root);
    if (!claim(slides, 'fkGallery')) return;

    const items = $$('[data-fk-slide]', slides);
    const thumbs = $$('[data-fk-thumb]', root);
    const current = $('[data-fk-current]', root);
    if (!items.length) return;

    const goTo = (index) => {
      items[index]?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    };

    // The sticky bar thumbnail mirrors whichever slide is showing — on a colour
    // change and on a manual swipe alike. Done here rather than trusting the
    // server markup, because Horizon morphs only parts of the section.
    const syncStickyThumb = (index) => {
      const thumb = $('.fk-sticky__img');
      const source = items[index]?.querySelector('img');
      if (!thumb || !source) return;
      const src = source.currentSrc || source.src;
      if (src) thumb.src = src.replace(/([?&]width=)\d+/, '$1160');
    };

    const setActive = (index) => {
      if (current) current.textContent = index + 1;
      thumbs.forEach((t, i) => t.classList.toggle('is-active', i === index));
      syncStickyThumb(index);
    };

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) setActive(items.indexOf(entry.target));
        }
      },
      { root: slides, threshold: 0.6 }
    );
    items.forEach((item) => observer.observe(item));

    thumbs.forEach((thumb, i) => thumb.addEventListener('click', () => goTo(i)));

    const step = (dir) => {
      const active = thumbs.findIndex((t) => t.classList.contains('is-active'));
      const next = (active + dir + items.length) % items.length;
      goTo(next);
    };

    $('[data-fk-prev]', root)?.addEventListener('click', () => step(-1));
    $('[data-fk-next]', root)?.addEventListener('click', () => step(1));

    // Liquid writes the selected variant's image index here. Horizon morphs the
    // section on a variant change rather than replacing it, so watching the
    // attribute is what catches a colour switch.
    const showActive = (behavior) => {
      const index = Number(slides.dataset.fkActive) || 0;
      items[index]?.scrollIntoView({ behavior, block: 'nearest', inline: 'center' });
      setActive(index);
    };

    new MutationObserver(() => showActive('smooth')).observe(slides, {
      attributes: true,
      attributeFilter: ['data-fk-active'],
    });

    // Move on the click itself rather than waiting for the section to come back
    // from the server. Liquid supplies colour -> gallery index.
    let mediaMap = null;
    try {
      mediaMap = JSON.parse(slides.dataset.fkMediaMap || '{}');
    } catch {
      mediaMap = {};
    }

    if (mediaMap && Object.keys(mediaMap).length) {
      root.addEventListener('change', (event) => {
        const input = event.target.closest('input[type="radio"][data-option-name]');
        if (!input) return;
        const index = mediaMap[input.value];
        if (index === undefined) return;
        slides.dataset.fkActive = index;
      });
    }

    showActive('auto');
  };

  /* ---------- Countdown ---------- */

  const initCountdown = (el) => {
    if (!claim(el, 'fkCountdown')) return;
    const end = new Date(el.dataset.end).getTime();
    if (Number.isNaN(end)) return;

    const out = {
      d: $('[data-cd-d]', el),
      h: $('[data-cd-h]', el),
      m: $('[data-cd-m]', el),
      s: $('[data-cd-s]', el),
    };

    const pad = (n) => String(n).padStart(2, '0');

    const tick = () => {
      const left = end - Date.now();

      // Once the offer is over the timer leaves rather than looping back round.
      if (left <= 0) {
        el.remove();
        clearInterval(timer);
        return;
      }

      const secs = Math.floor(left / 1000);
      out.d.textContent = pad(Math.floor(secs / 86400));
      out.h.textContent = pad(Math.floor((secs % 86400) / 3600));
      out.m.textContent = pad(Math.floor((secs % 3600) / 60));
      out.s.textContent = pad(secs % 60);
      el.hidden = false;
    };

    tick();
    const timer = setInterval(tick, 1000);
  };

  /* ---------- Quantity ---------- */

  const initQty = (root) => {
    const input = $('[data-fk-qty-input]', root);
    if (!claim(input, 'fkQty')) return;

    const clamp = (v) => Math.max(Number(input.min) || 1, Math.min(Number(input.max) || Infinity, v));

    $('[data-fk-qty-down]', root)?.addEventListener('click', () => {
      input.value = clamp(Number(input.value) - 1);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });

    $('[data-fk-qty-up]', root)?.addEventListener('click', () => {
      input.value = clamp(Number(input.value) + 1);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  };

  /* ---------- Buy now ---------- */

  const initBuyNow = (root) => {
    const button = $('[data-fk-buy-now]', root);
    const form = $('form[action*="/cart/add"]', root);
    if (!form || !claim(button, 'fkBuyNow')) return;

    button.addEventListener('click', async () => {
      button.disabled = true;
      try {
        const response = await fetch(`${window.Shopify?.routes?.root ?? '/'}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            id: form.querySelector('[name="id"]').value,
            quantity: Number(form.querySelector('[name="quantity"]')?.value) || 1,
          }),
        });
        if (!response.ok) throw new Error('Add to cart failed');
        window.location.href = `${window.Shopify?.routes?.root ?? '/'}checkout`;
      } catch (error) {
        console.error('[fakhiz] buy now', error);
        button.disabled = false;
      }
    });
  };

  /* ---------- Sticky buy bar ---------- */

  const initStickyBar = (root) => {
    const bar = $('[data-fk-sticky]');
    const anchor = $('[data-fk-atc]', root);
    if (!anchor || !claim(bar, 'fkSticky')) return;

    const wa = $('[data-fk-wa]');
    bar.hidden = false;

    // The bar is one row on desktop and two or three on a phone, so the
    // WhatsApp button clears it by its real height rather than a fixed guess.
    const publishHeight = () => {
      document.documentElement.style.setProperty('--fk-sticky-h', `${bar.offsetHeight}px`);
    };
    publishHeight();
    if ('ResizeObserver' in window) new ResizeObserver(publishHeight).observe(bar);
    else window.addEventListener('resize', publishHeight);

    // Each sticky select drives the matching radio in the main picker, so the
    // variant-picker component stays the only thing resolving the variant.
    for (const select of $$('[data-fk-sticky-option]', bar)) {
      select.addEventListener('change', () => {
        const name = select.dataset.fkStickyOption;
        const radio = $$(`input[type="radio"][data-option-name="${CSS.escape(name)}"]`, root).find(
          (input) => input.value === select.value
        );
        if (radio && !radio.checked) radio.click();
      });
    }

    // ...and the reverse. Without this the bar keeps whatever it was rendered
    // with, so after choosing a colour above it shows a stale value — and
    // re-picking that value fires no change event, leaving the bar stuck.
    root.addEventListener('change', (event) => {
      const input = event.target.closest('input[type="radio"][data-option-name]');
      if (!input || !input.checked) return;
      const select = $(`[data-fk-sticky-option="${CSS.escape(input.dataset.optionName)}"]`, bar);
      if (select && select.value !== input.value) select.value = input.value;
    });

    const watcher = new IntersectionObserver(
      ([entry]) => {
        const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        bar.classList.toggle('is-visible', show);
        // Keep the WhatsApp button from sitting on top of the buy bar.
        wa?.classList.toggle('is-raised', show);
      },
      { threshold: 0 }
    );
    watcher.observe(anchor);

    $('[data-fk-sticky-add]', bar)?.addEventListener('click', () => anchor.click());
  };

  /* ---------- Cart drawer ---------- */

  const ROOT = () => window.Shopify?.routes?.root ?? '/';

  // Mirrors Shopify's money_format placeholders closely enough for the few
  // figures shown here, without pulling in a formatting library.
  const formatMoney = (cents, format) => {
    const withCommas = (n, decimals) => {
      const fixed = (n / 100).toFixed(decimals);
      const [whole, frac] = fixed.split('.');
      const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return frac ? `${grouped}.${frac}` : grouped;
    };

    if (!format) return withCommas(cents, 2);

    return format.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, token) =>
      token.includes('no_decimals') ? withCommas(cents, 0) : withCommas(cents, 2)
    );
  };

  const initCart = () => {
    const root = $('[data-fk-cart]');
    // boot() also runs on section reload; the document-level listeners below must
    // only ever be attached once.
    if (!root || root.dataset.ready === 'true') return;
    root.dataset.ready = 'true';

    const body = $('[data-fk-cart-body]', root);
    const foot = $('[data-fk-cart-foot]', root);
    const countEl = $('[data-fk-cart-count]', root);
    const subtotalEl = $('[data-fk-cart-subtotal]', root);
    const shipText = $('[data-fk-ship-text]', root);
    const shipFill = $('[data-fk-ship-fill]', root);
    const truck = $('[data-fk-ship-truck]', root);
    const money = root.dataset.moneyFormat;
    const threshold = Number(root.dataset.threshold) || 0;

    let busy = false;

    const open = ({ reload = true } = {}) => {
      root.hidden = false;
      void root.offsetWidth;
      root.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      if (reload) refresh();
    };

    const close = () => {
      root.classList.remove('is-open');
      document.body.style.overflow = '';
      setTimeout(() => {
        root.hidden = true;
      }, 320);
    };

    let lastPercent = null;

    // Replays the drive animation only when the position actually changes, so
    // reopening the drawer on an unchanged cart stays still.
    const moveTruck = (percent, arrived) => {
      if (!truck) return;

      const changed = lastPercent !== null && Math.abs(percent - lastPercent) > 0.5;
      truck.style.setProperty('--pos', `${percent}%`);
      truck.classList.toggle('is-arrived', arrived);

      if (changed) {
        truck.classList.remove('is-driving');
        void truck.offsetWidth;
        truck.classList.add('is-driving');
        setTimeout(() => truck.classList.remove('is-driving'), 700);
      }

      lastPercent = percent;
    };

    const renderShipping = (total) => {
      if (!shipText || !shipFill || !threshold) return;

      const remaining = threshold - total;

      if (remaining <= 0) {
        shipText.innerHTML = '<b>Free delivery unlocked.</b>';
        shipFill.style.width = '100%';
        shipFill.classList.add('is-full');
        moveTruck(100, true);
        return;
      }

      const percent = Math.max((total / threshold) * 100, 0);
      shipText.innerHTML = `<b>${formatMoney(remaining, money)}</b> away from free delivery`;
      shipFill.style.width = `${Math.max(percent, 2)}%`;
      shipFill.classList.remove('is-full');
      moveTruck(percent, false);
    };

    // /cart.js reports the chosen variant but not the product's other variants,
    // so each product is fetched once and cached to build the line selects.
    const productCache = new Map();

    const loadProduct = async (handle) => {
      if (productCache.has(handle)) return productCache.get(handle);
      try {
        const res = await fetch(`${ROOT()}products/${handle}.js`, { headers: { Accept: 'application/json' } });
        if (!res.ok) throw new Error(`product ${res.status}`);
        const data = await res.json();
        productCache.set(handle, data);
        return data;
      } catch (error) {
        console.error('[fakhiz] product fetch', error);
        productCache.set(handle, null);
        return null;
      }
    };

    const optionSelects = (product, item) => {
      if (!product || product.variants.length < 2) return '';

      return product.options
        .map((option, index) => {
          const name = typeof option === 'string' ? option : option.name;
          const values = [...new Set(product.variants.map((v) => v.options[index]))];
          const current = item.options_with_values?.[index]?.value ?? item.variant_options?.[index];

          const opts = values
            .map((value) => {
              const selected = value === current ? ' selected' : '';
              return `<option value="${value}"${selected}>${value}</option>`;
            })
            .join('');

          return `<label class="fk-line__select">
              <span class="fk-sr">${name}</span>
              <select data-fk-line-option data-index="${index}" aria-label="${name}">${opts}</select>
            </label>`;
        })
        .join('');
    };

    // Shopify cannot re-point a cart line at another variant, so the swap sets
    // the old variant's quantity to zero and the new one's to the same count,
    // in a single atomic /cart/update.js call.
    const swapVariant = async (key, handle, index, value) => {
      // Claimed before the first await: loadProduct yields, and without this a
      // second select change could slip past the busy check in the handler.
      if (busy) return;
      busy = true;

      // /cart/update.js sets absolute quantities, so a queued +/- must land
      // before this reads cartState — otherwise the swap writes a stale count.
      await flushWrites();

      const product = await loadProduct(handle);
      const line = cartState?.items.find((i) => i.key === key);
      if (!product || !line) {
        busy = false;
        return;
      }

      const wanted = [...(line.options_with_values?.map((o) => o.value) ?? line.variant_options ?? [])];
      wanted[index] = value;

      const match = product.variants.find((v) => v.options.every((opt, i) => opt === wanted[i]));

      if (!match) {
        busy = false;
        return;
      }
      if (!match.available) {
        busy = false;
        await refresh();
        return;
      }

      // Quantities in /cart/update.js are absolute and keyed by variant, so
      // if the target variant is already in the cart the two lines merge
      // rather than sitting side by side.
      const alreadyThere = cartState.items
        .filter((i) => i.id === match.id)
        .reduce((n, i) => n + i.quantity, 0);

      root.classList.add('is-busy');
      try {
        // One atomic request. The old add-then-remove pair could leave both
        // lines in the cart if the second call was slow or failed.
        const res = await fetch(`${ROOT()}cart/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            updates: {
              [line.id]: 0,
              [match.id]: alreadyThere + line.quantity,
            },
          }),
        });
        if (!res.ok) throw new Error(`update ${res.status}`);
        render(await res.json());
      } catch (error) {
        console.error('[fakhiz] variant swap', error);
        await refresh();
      } finally {
        busy = false;
        root.classList.remove('is-busy');
      }
    };

    let cartState = null;

    const render = (cart) => {
      // A malformed response must not blank the drawer or throw out of a
      // caller that has no catch around it.
      if (!cart || !Array.isArray(cart.items)) {
        console.error('[fakhiz] unexpected cart payload', cart);
        return;
      }

      cartState = cart;
      if (countEl) countEl.textContent = cart.item_count;

      const bubble = $('[data-fk-cart-bubble]');
      if (bubble) {
        bubble.textContent = cart.item_count;
        bubble.hidden = cart.item_count === 0;
      }

      renderShipping(cart.total_price);

      if (!cart.items.length) {
        body.innerHTML = '<p class="fk-cart__empty">Your cart is empty.</p>';
        if (foot) foot.hidden = true;
        return;
      }

      if (foot) foot.hidden = false;
      if (subtotalEl) subtotalEl.textContent = formatMoney(cart.total_price, money);

      body.innerHTML = cart.items
        .map((item) => {
          const img = item.image
            ? `<img src="${item.image.replace(/(\.[a-z]+)(\?|$)/i, '_160x$1$2')}" alt="" loading="lazy">`
            : '';
          const variant = item.variant_title ? `<span class="fk-line__variant">${item.variant_title}</span>` : '';
          return `
            <article class="fk-line" data-key="${item.key}" data-handle="${item.handle}">
              <div class="fk-line__media">${img}</div>
              <div class="fk-line__info">
                <span class="fk-line__title">${item.product_title}</span>
                ${variant}
                <div class="fk-line__options" data-fk-line-options></div>
                <span class="fk-line__price">${formatMoney(item.final_line_price, money)}</span>
                <div class="fk-line__actions">
                  <div class="fk-line__qty">
                    <button type="button" data-fk-line-down aria-label="Decrease quantity">−</button>
                    <span data-fk-line-qty>${item.quantity}</span>
                    <button type="button" data-fk-line-up aria-label="Increase quantity">+</button>
                  </div>
                  <button class="fk-line__remove" type="button" data-fk-line-remove>Remove</button>
                </div>
              </div>
            </article>`;
        })
        .join('');

      // Option selects fill in once each product's variant list has loaded, so
      // the cart renders immediately rather than waiting on those requests.
      for (const item of cart.items) {
        if (!item.handle) continue;
        loadProduct(item.handle).then((product) => {
          const line = body.querySelector(`[data-key="${CSS.escape(item.key)}"] [data-fk-line-options]`);
          if (line) line.innerHTML = optionSelects(product, item);
        });
      }
    };

    // A skeleton beats a spinner: the drawer keeps its shape while the add
    // completes, so nothing jumps when the real lines arrive.
    const skeleton = (rows = 1) => {
      body.innerHTML = Array.from({ length: rows })
        .map(() => '<div class="fk-skel__line" aria-hidden="true"></div>')
        .join('');
      body.setAttribute('aria-busy', 'true');
    };

    const esc = (value) =>
      String(value ?? '').replace(/[&<>"']/g, (c) =>
        ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]
      );

    // An optimistic line, built from the product page itself, so the drawer
    // shows what was added straight away instead of after Shopify's round
    // trip. It carries no data-key, so the quantity and remove handlers
    // ignore it, and the authoritative cart replaces it on resolve.
    const optimisticLine = (lines) => {
      const title = $('.fk-buy__title')?.textContent.trim();
      if (!title) return false;

      const price = $('.fk-price__now')?.textContent.trim() || '';
      const chosen = $$('input[type="radio"][data-option-name]')
        .filter((input) => input.checked)
        .map((input) => input.value)
        .join(' / ');
      const source = $('.fk-sticky__img') || $('.fk-gal__slides img');
      const src = source?.currentSrc || source?.src || '';
      const qty = lines?.[0]?.quantity || 1;

      body.innerHTML = `
        <article class="fk-line fk-line--pending" aria-busy="true">
          <div class="fk-line__media">${src ? `<img src="${esc(src)}" alt="">` : ''}</div>
          <div class="fk-line__info">
            <span class="fk-line__title">${esc(title)}</span>
            ${chosen ? `<span class="fk-line__variant">${esc(chosen)}</span>` : ''}
            <span class="fk-line__price">${esc(price)}</span>
            <div class="fk-line__actions">
              <div class="fk-line__qty"><span data-fk-line-qty>${qty}</span></div>
            </div>
          </div>
        </article>`;
      return true;
    };

    // Horizon already fetched /cart.js to settle its own promise and hands the
    // items back on the payload. Rendering from that saves a third sequential
    // round trip on every add.
    const cartFromPayload = (value) => {
      const items = value?.detail?.items;
      if (!Array.isArray(items) || !items.length) return null;
      let count = 0;
      let subtotal = 0;
      for (const item of items) {
        count += item.quantity || 0;
        subtotal += item.final_line_price ?? item.line_price ?? 0;
      }
      return {
        items,
        item_count: count,
        items_subtotal_price: subtotal,
        total_price: subtotal,
      };
    };

    const refresh = async () => {
      try {
        const res = await fetch(`${ROOT()}cart.js`, { headers: { Accept: 'application/json' } });
        if (!res.ok) throw new Error(`cart ${res.status}`);
        render(await res.json());
      } catch (error) {
        console.error('[fakhiz] cart fetch', error);
        body.innerHTML = '<p class="fk-cart__empty">Could not load your cart.</p>';
      }
    };

    // Quantity edits and removals are applied locally first and the request
    // follows. Waiting on the network before redrawing is what made removing
    // an item feel slow: one round trip of a dimmed, frozen cart per click.
    const pendingWrites = new Map();

    const projectCart = (key, quantity) => {
      if (!cartState) return null;

      const items = [];
      let count = 0;
      let subtotal = 0;

      for (const item of cartState.items) {
        if (item.key === key) {
          if (quantity <= 0) continue;
          // Unit price from the line keeps any per-item discount roughly
          // right; the server response corrects it a moment later.
          const unit = item.quantity ? item.final_line_price / item.quantity : 0;
          const listUnit = item.quantity ? item.line_price / item.quantity : 0;
          const next = {
            ...item,
            quantity,
            final_line_price: Math.round(unit * quantity),
            line_price: Math.round(listUnit * quantity),
          };
          items.push(next);
          count += quantity;
          subtotal += next.final_line_price;
          continue;
        }
        items.push(item);
        count += item.quantity;
        subtotal += item.final_line_price;
      }

      return {
        ...cartState,
        items,
        item_count: count,
        items_subtotal_price: subtotal,
        total_price: subtotal,
      };
    };

    const sendChange = async (key, quantity) => {
      try {
        const res = await fetch(`${ROOT()}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, quantity }),
        });
        if (!res.ok) throw new Error(`change ${res.status}`);
        const server = await res.json();
        // A later click may already have moved on; that write will redraw.
        if (!pendingWrites.size) render(server);
      } catch (error) {
        console.error('[fakhiz] cart change', error);
        await refresh();
      }
    };

    // Anything that needs an authoritative cart settles queued writes first.
    const flushWrites = async () => {
      for (const key of [...pendingWrites.keys()]) {
        const entry = pendingWrites.get(key);
        if (!entry) continue;
        clearTimeout(entry.timer);
        pendingWrites.delete(key);
        await entry.run();
      }
    };

    const change = (key, quantity) => {
      const optimistic = projectCart(key, quantity);
      if (optimistic) render(optimistic);

      const existing = pendingWrites.get(key);
      if (existing) clearTimeout(existing.timer);

      const run = () => sendChange(key, quantity);
      // Short coalescing window so holding "+" sends one request, not six.
      const timer = setTimeout(() => {
        pendingWrites.delete(key);
        run();
      }, 220);

      pendingWrites.set(key, { timer, run });
    };

    body.addEventListener('click', (event) => {
      const line = event.target.closest('[data-key]');
      if (!line) return;
      const key = line.dataset.key;
      const current = Number($('[data-fk-line-qty]', line).textContent) || 1;

      if (event.target.closest('[data-fk-line-up]')) change(key, current + 1);
      else if (event.target.closest('[data-fk-line-down]')) change(key, current - 1);
      else if (event.target.closest('[data-fk-line-remove]')) change(key, 0);
    });

    body.addEventListener('change', (event) => {
      const select = event.target.closest('[data-fk-line-option]');
      if (!select || busy) return;
      const line = select.closest('[data-key]');
      if (!line) return;
      swapVariant(line.dataset.key, line.dataset.handle, Number(select.dataset.index), select.value);
    });

    for (const el of $$('[data-fk-cart-close]', root)) el.addEventListener('click', close);
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && root.classList.contains('is-open')) close();
    });

    for (const el of $$('[data-fk-cart-open]')) {
      el.addEventListener('click', (event) => {
        event.preventDefault();
        open();
      });
    }

    // Horizon broadcasts this the moment an add STARTS — the network request is
    // still in flight — and dispatches it on the product form, which does not
    // reliably bubble. Listening in the capture phase catches it either way,
    // and the promise it carries resolves once the cart really has the item.
    // Refreshing without waiting raced the POST and showed an empty drawer on
    // the first add.
    document.addEventListener(
      'shopify:cart:lines-update',
      (event) => {
        if (!root.classList.contains('is-open')) open({ reload: false });
        root.classList.add('is-busy');

        // Only replace the body when there is nothing real in it; an existing
        // cart stays put and simply updates when the add lands.
        if (!body.querySelector('[data-key]')) {
          if (!optimisticLine(event?.lines)) skeleton(Math.max(1, event?.lines?.length || 1));
        }

        let settled = false;
        const done = (value) => {
          if (settled) return;
          settled = true;
          root.classList.remove('is-busy');
          body.removeAttribute('aria-busy');

          const direct = cartFromPayload(value);
          if (direct) render(direct);
          else refresh();
        };

        const pending = event?.promise;
        if (pending && typeof pending.then === 'function') {
          pending.then(done, () => done());
          // A rejected or never-settled promise must not leave the drawer stuck.
          // Long, because this is a safety net for a hung promise — not a
          // deadline. A short one fired before the real response and kicked
          // off a /cart.js that finished later than simply waiting.
          setTimeout(() => done(), 8000);
        } else {
          done();
        }
      },
      true
    );

    refresh();
  };

  /* ---------- Size guide ---------- */

  const initSizeGuide = () => {
    const modal = $('[data-fk-sizeguide]');
    if (!modal || modal.dataset.ready === 'true') return;
    modal.dataset.ready = 'true';

    let lastFocused = null;

    const open = () => {
      lastFocused = document.activeElement;
      modal.hidden = false;
      void modal.offsetWidth;
      modal.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      // The scrim also carries the close hook but is not focusable, so target the
      // button specifically — otherwise focus stays behind the dialog.
      $('button[data-fk-sizeguide-close]', modal)?.focus();
    };

    const close = () => {
      modal.classList.remove('is-open');
      document.body.style.overflow = '';
      setTimeout(() => {
        modal.hidden = true;
      }, 260);
      lastFocused?.focus();
    };

    for (const el of $$('[data-fk-sizeguide-open]')) {
      el.addEventListener('click', (event) => {
        event.preventDefault();
        open();
      });
    }

    for (const el of $$('[data-fk-sizeguide-close]', modal)) el.addEventListener('click', close);

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && modal.classList.contains('is-open')) close();
    });
  };

  /* ---------- Live stock strip ---------- */

  const initStockStrip = (root) => {
    const strip = $('[data-fk-urg]', root);
    if (!claim(strip, 'fkStock')) return;

    let map = null;
    try {
      map = JSON.parse(strip.dataset.fkStockMap || '{}');
    } catch {
      map = {};
    }
    if (!map || !Object.keys(map).length) return;

    const labels = $$('[data-fk-urg-text]', strip);
    if (!labels.length) return;

    const update = () => {
      // Option radios render in option order, so joining the checked values
      // in DOM order reproduces Shopify's variant title exactly.
      const title = $$('input[type="radio"][data-option-name]', root)
        .filter((input) => input.checked)
        .map((input) => input.value)
        .join(' / ');

      const qty = map[title];
      if (qty === undefined) return;

      if (qty > 0) {
        strip.hidden = false;
        for (const label of labels) label.textContent = `${qty} stocks left`;
      } else {
        // Sold-out combinations get no count at all rather than "0 left".
        strip.hidden = true;
      }
    };

    root.addEventListener('change', (event) => {
      if (event.target.closest('input[type="radio"][data-option-name]')) update();
    });

    update();
  };

  /* ---------- Review photo lightbox ---------- */

  const initReviewLightbox = () => {
    const grid = $('[data-fk-rev-grid]');
    if (!grid || !claim(grid, 'fkRevLightbox')) return;

    let box = null;
    let lastFocused = null;

    const close = () => {
      if (!box) return;
      box.classList.remove('is-open');
      document.body.style.overflow = '';
      setTimeout(() => {
        box?.remove();
        box = null;
      }, 220);
      lastFocused?.focus();
    };

    const open = (src, alt) => {
      close();
      lastFocused = document.activeElement;

      box = document.createElement('div');
      box.className = 'fk fk-lb';
      box.setAttribute('role', 'dialog');
      box.setAttribute('aria-modal', 'true');
      box.setAttribute('aria-label', alt || 'Customer photo');
      box.innerHTML =
        '<div class="fk-lb__scrim" data-fk-lb-close></div>' +
        '<figure class="fk-lb__frame">' +
        '<img src="" alt="">' +
        '<button class="fk-lb__close" type="button" data-fk-lb-close aria-label="Close">&times;</button>' +
        '</figure>';

      const img = $('img', box);
      img.src = src;
      img.alt = alt || '';

      document.body.appendChild(box);
      // Force layout before the class lands, so the transition actually runs.
      void box.offsetWidth;
      box.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      $('.fk-lb__close', box)?.focus();

      box.addEventListener('click', (event) => {
        if (event.target.closest('[data-fk-lb-close]')) close();
      });
    };

    grid.addEventListener('click', (event) => {
      const shot = event.target.closest('.fk-rev__shot');
      if (!shot) return;
      const img = $('img', shot);
      if (!img) return;
      open(img.currentSrc || img.src, img.alt);
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && box) close();
    });
  };

  /* ---------- Reviews: show more ---------- */

  const initReviewsMore = () => {
    const grid = $('[data-fk-rev-grid]');
    const wrap = $('[data-fk-rev-more]');
    if (!grid || !wrap || !claim(wrap, 'fkRevMore')) return;

    const button = $('[data-fk-rev-toggle]', wrap);
    const cards = $$('.fk-rev__card', grid);
    if (!button || !cards.length) return;

    // How many are visible is a CSS decision that changes with the
    // breakpoint, so count what the layout actually hid rather than assume.
    const hidden = () => cards.filter((card) => card.offsetParent === null).length;

    const remaining = hidden();
    if (!remaining) return;

    const moreLabel = button.dataset.more || 'View more reviews';
    const lessLabel = button.dataset.less || 'Show fewer';

    button.textContent = `${moreLabel} (${remaining})`;
    wrap.hidden = false;

    button.addEventListener('click', () => {
      const expanded = grid.classList.toggle('is-expanded');
      button.textContent = expanded ? lessLabel : `${moreLabel} (${hidden()})`;
      button.setAttribute('aria-expanded', String(expanded));

      // Collapsing from the bottom would leave the reader stranded below the
      // section, so bring the list back into view.
      if (!expanded) {
        grid.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
  };

  /* ---------- Review form ---------- */

  const initReviewForm = () => {
    const wrap = $('[data-fk-review-form]');
    if (!wrap || wrap.dataset.ready === 'true') return;
    wrap.dataset.ready = 'true';

    const toggle = () => {
      const opening = wrap.hidden;
      wrap.hidden = !opening;
      if (opening) {
        wrap.scrollIntoView({ behavior: 'smooth', block: 'center' });
        $('input, textarea', wrap)?.focus({ preventScroll: true });
      }
    };

    for (const el of $$('[data-fk-review-toggle]')) el.addEventListener('click', toggle);
  };

  /* ---------- Scroll reveal ---------- */

  const initReveal = () => {
    const targets = $$('.fk-reveal').filter((el) => claim(el, 'fkReveal'));
    if (!targets.length) return;

    if (!('IntersectionObserver' in window)) {
      targets.forEach((el) => el.classList.add('is-in'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          entry.target.classList.add('is-in');
          observer.unobserve(entry.target);
        }
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
    );

    targets.forEach((el) => observer.observe(el));
  };

  /* ---------- Boot ---------- */

  const boot = () => {
    initHeader();
    initReveal();
    initCart();
    initSizeGuide();
    initReviewForm();
    initReviewsMore();
    initReviewLightbox();

    $$('[data-fk-countdown]').forEach(initCountdown);

    const product = $('[data-fk-product]');
    if (product) {
      initGallery(product);
      initQty(product);
      initBuyNow(product);
      initStickyBar(product);
      initStockStrip(product);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  // Sections re-render in the theme editor and after variant changes.
  document.addEventListener('shopify:section:load', boot);

})();
